import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Play, 
  CheckCircle, 
  Globe, 
  Languages, 
  MapPin, 
  Zap,
  Target,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import { ExecuteGlobalTranslationEnforcement } from '@/utils/execution/ExecuteGlobalTranslationEnforcement';
import { languageNames, languageFlags } from '@/context/languages/languageData';

const TranslationExecutionPage = () => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionLog, setExecutionLog] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // Batched logging to prevent UI blocking
  const logBufferRef = useRef<string[]>([]);
  const flushTimerRef = useRef<number | null>(null);

  const pushLog = (...args: any[]) => {
    const message = args.map(arg => 
      typeof arg === 'string' ? arg : 
      typeof arg === 'object' ? '[object]' : 
      String(arg)
    ).join(' ');
    
    if (!message.trim()) return;
    
    const timestampedMessage = `${new Date().toLocaleTimeString()}: ${message}`;
    logBufferRef.current.push(timestampedMessage);
    
    // Schedule flush if not already scheduled
    if (flushTimerRef.current === null) {
      flushTimerRef.current = window.setTimeout(() => {
        setExecutionLog(prev => {
          const newLogs = [...prev, ...logBufferRef.current];
          logBufferRef.current = [];
          const MAX_LOGS = 400;
          return newLogs.slice(-MAX_LOGS); // Keep only last 400 lines
        });
        flushTimerRef.current = null;
      }, 200);
    }
  };

  const forceFlushLogs = () => {
    if (logBufferRef.current.length > 0) {
      setExecutionLog(prev => {
        const newLogs = [...prev, ...logBufferRef.current];
        logBufferRef.current = [];
        const MAX_LOGS = 400;
        return newLogs.slice(-MAX_LOGS);
      });
    }
    if (flushTimerRef.current !== null) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  };

  const addToLog = (message: string) => {
    setExecutionLog(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const handleFullExecution = async () => {
    setIsExecuting(true);
    setProgress(0);
    setExecutionLog([]);
    setIsComplete(false);
    logBufferRef.current = [];

    // Override console.log to capture output
    const originalLog = console.log;
    let phaseProgress = 0;
    
    console.log = (...args: any[]) => {
      originalLog(...args);
      pushLog(...args);
      
      const message = args.join(' ');
      
      // Update progress based on phase markers
      if (message.includes('PHASE 1')) {
        phaseProgress = 10;
        setProgress(10);
      } else if (message.includes('PHASE 2')) {
        phaseProgress = 30;
        setProgress(30);
      } else if (message.includes('PHASE 3')) {
        phaseProgress = 60;
        setProgress(60);
      } else if (message.includes('PHASE 4')) {
        phaseProgress = 90;
        setProgress(90);
      } else if (message.includes('Scanning chunk')) {
        // Parse "Scanning chunk X/Y" to show progress 10→30
        const match = message.match(/chunk (\d+)\/(\d+)/);
        if (match) {
          const current = parseInt(match[1]);
          const total = parseInt(match[2]);
          const chunkProgress = 10 + Math.floor((current / total) * 20);
          setProgress(chunkProgress);
        }
      }
    };

    try {
      pushLog('🚀 Starting Global Translation Enforcement...');
      
      // Defer execution to prevent blocking the main thread
      await new Promise(resolve => setTimeout(resolve, 100));
      
      await ExecuteGlobalTranslationEnforcement.execute();
      
      setProgress(100);
      setIsComplete(true);
      pushLog('🎉 Global Translation Enforcement Complete!');
    } catch (error) {
      pushLog(`❌ Execution failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      console.log = originalLog;
      forceFlushLogs();
      setIsExecuting(false);
    }
  };

  const handleQuickTest = async () => {
    setIsExecuting(true);
    setProgress(0);
    setExecutionLog([]);
    setIsComplete(false);
    logBufferRef.current = [];

    const originalLog = console.log;
    console.log = (...args: any[]) => {
      originalLog(...args);
      pushLog(...args);
      setProgress(prev => Math.min(prev + 10, 95));
    };

    try {
      pushLog('⚡ Running Quick Translation Test...');
      await new Promise(resolve => setTimeout(resolve, 100));
      await ExecuteGlobalTranslationEnforcement.executeQuick();
      setProgress(100);
      setIsComplete(true);
      pushLog('✅ Quick test completed successfully!');
    } catch (error) {
      pushLog(`❌ Quick test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      console.log = originalLog;
      forceFlushLogs();
      setIsExecuting(false);
    }
  };

  const handleValidateUrls = () => {
    setExecutionLog([]);
    logBufferRef.current = [];
    const originalLog = console.log;
    console.log = (...args: any[]) => {
      originalLog(...args);
      pushLog(...args);
    };

    try {
      pushLog('🔍 Validating URL Patterns...');
      ExecuteGlobalTranslationEnforcement.validateUserRequestedUrls();
      pushLog('✅ URL Validation Complete!');
    } catch (error) {
      pushLog(`❌ Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      console.log = originalLog;
      forceFlushLogs();
    }
  };

  return (
    <>
      <Helmet>
        <title>Translation Execution Center</title>
        <meta name="description" content="Execute global translation enforcement and validation" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
              <Globe className="h-8 w-8" />
              Translation Execution Center
            </h1>
            <p className="text-muted-foreground">
              Execute comprehensive translation enforcement for all cities and languages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Target URLs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 text-sm">
                  <div>✅ /city/hamburg (EN)</div>
                  <div>✅ /de/city/hamburg (DE)</div>
                  <div>✅ /city/hamburg/anxiety (EN)</div>
                  <div>✅ /de/city/hamburg/anxiety (DE)</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Languages className="h-4 w-4" />
                  Languages ({Object.keys(languageNames).length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                  {Object.entries(languageNames).map(([code, name]) => (
                    <Badge key={code} variant="outline" className="text-xs flex items-center gap-1">
                      <span>{languageFlags[code as keyof typeof languageFlags]}</span>
                      <span>{code.toUpperCase()}</span>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Goal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">100%</div>
                <div className="text-sm text-muted-foreground">Translation Coverage</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Play className="h-5 w-5" />
                  Execution Controls
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Full execution processes all cities and languages. This may take several minutes.
                  </AlertDescription>
                </Alert>

                <div className="space-y-3">
                  <Button 
                    onClick={handleFullExecution}
                    disabled={isExecuting}
                    className="w-full flex items-center gap-2"
                    size="lg"
                  >
                    {isExecuting ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Globe className="h-4 w-4" />
                    )}
                    {isExecuting ? 'Executing...' : 'Execute Full Translation Enforcement'}
                  </Button>

                  <Button 
                    onClick={handleQuickTest}
                    disabled={isExecuting}
                    variant="outline"
                    className="w-full flex items-center gap-2"
                  >
                    <Zap className="h-4 w-4" />
                    Quick Test (Hamburg, Madrid, Paris, Berlin)
                  </Button>

                  <Button 
                    onClick={handleValidateUrls}
                    disabled={isExecuting}
                    variant="secondary"
                    className="w-full flex items-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Validate URL Patterns
                  </Button>
                </div>

                {isExecuting && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span>{progress.toFixed(0)}%</span>
                    </div>
                    <Progress value={progress} className="w-full" />
                  </div>
                )}

                {isComplete && (
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      ✅ Translation enforcement completed successfully! All URLs now have proper localization and 100% translated content.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Execution Log</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-muted p-4 rounded-lg max-h-96 overflow-y-auto">
                  {executionLog.length === 0 ? (
                    <div className="text-muted-foreground text-sm">
                      Execution log will appear here...
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {executionLog.map((line, index) => (
                        <div 
                          key={index} 
                          className="text-xs font-mono break-words"
                        >
                          {line}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default TranslationExecutionPage;